import { Button, message, Space, Spin } from "antd";
import { CloseOutlined, DownloadOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { marked } from "marked";
import { apiUrl } from "../../server";
import { serverFetch } from "../../server";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";

const PluginView = ({ plugin, user, onCancel }) => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [comments, setComments] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const imagesWithPhoto = images.filter((img) => img.url);
  const currentIndex = imagesWithPhoto.length ? Math.min(selectedImageIndex, imagesWithPhoto.length - 1) : 0;
  const currentImage = imagesWithPhoto[currentIndex];
  const hasDescription = item?.description != null && String(item.description).trim() !== "";
  const hasSubscription = user?.support;

  const loadPlugin = () => {
    if (!plugin?.id) return;
    setLoading(true);
    serverFetch(`Plugin/plugins/${plugin.id}`)
      .then((data) => {
        setItem(data);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e?.userMessage || "Ошибка загрузки плагина");
      });
  };

  useEffect(() => {
    if (plugin?.id) loadPlugin();
    else if (plugin) {
      setItem(plugin);
      setLoading(false);
    }
  }, [plugin?.id, plugin]);

  const loadImages = () => {
    if (!plugin?.id) return;
    setLoadingImages(true);
    serverFetch("Plugin/images", { queryParams: { pluginId: plugin.id } })
      .then((data) => setImages(Array.isArray(data) ? data : []))
      .catch(() => setImages([]))
      .finally(() => setLoadingImages(false));
  };

  const loadComments = () => {
    if (!plugin?.id) return;
    setLoadingComments(true);
    serverFetch("Plugin/comments", { queryParams: { pluginId: plugin.id } })
      .then((data) => setComments(Array.isArray(data) ? data : []))
      .catch(() => setComments([]))
      .finally(() => setLoadingComments(false));
  };

  const loadFiles = () => {
    if (!plugin?.id) return;
    setLoadingFiles(true);
    serverFetch(`Plugin/files/list/${plugin.id}`)
      .then((data) => setFiles(Array.isArray(data) ? data : []))
      .catch(() => setFiles([]))
      .finally(() => setLoadingFiles(false));
  };

  useEffect(() => {
    loadImages();
    loadComments();
    loadFiles();
  }, [plugin?.id]);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [plugin?.id]);

  const downloadFile = (fileName) => {
    if (!hasSubscription) return;
    const sessionId = localStorage.getItem("sessionId");
    const url = `${apiUrl}/api/Plugin/files/${plugin.id}/${encodeURIComponent(fileName)}`;
    fetch(url, { headers: sessionId ? { SessionId: sessionId } : {} })
      .then((r) => {
        if (!r.ok) return r.json().then((e) => Promise.reject(e));
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => message.error(e?.userMessage || e?.message || "Ошибка скачивания"));
  };

  /** Имя файла из заголовка Content-Disposition (сервер присылает, например: plugin_{Name}_{Ver}.zip). */
  const getFileNameFromResponse = (response) => {
    const disposition = response.headers.get("Content-Disposition");
    if (!disposition) return null;
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) return decodeURIComponent(utf8Match[1].trim());
    const quotedMatch = disposition.match(/filename="([^"]*)"/i);
    if (quotedMatch) return quotedMatch[1].trim();
    const unquotedMatch = disposition.match(/filename=([^;\s]+)/i);
    return unquotedMatch ? unquotedMatch[1].trim() : null;
  };

  const downloadAllFiles = () => {
    if (!hasSubscription) return;
    const sessionId = localStorage.getItem("sessionId");
    const url = `${apiUrl}/api/Plugin/files/pack/${plugin.id}`;
    fetch(url, { headers: sessionId ? { SessionId: sessionId } : {} })
      .then((r) => {
        if (!r.ok) return r.json().then((e) => Promise.reject(e));
        return r.blob().then((blob) => ({ blob, response: r }));
      })
      .then(({ blob, response }) => {
        const zipName = getFileNameFromResponse(response) ?? `Решение_${plugin.name}_${item?.ver ?? 1}.zip`;
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = zipName;
        a.click();
        URL.revokeObjectURL(a.href);
        message.success("Архив скачан");
      })
      .catch((e) => message.error(e?.userMessage || e?.message || "Ошибка скачивания архива"));
  };

  if (!plugin) return null;
  if (plugin.id && (loading || !item)) {
    if (isMobile) {
      return (
        <>
          <div className="company-base-sheet-header">
            <span className="company-base-sheet-title">Плагин</span>
            <Space size="small" className="company-base-sheet-actions">
              <Button
                type="default"
                icon={<CloseOutlined />}
                onClick={onCancel}
                aria-label="Закрыть"
                className="company-base-header-icon-btn"
              />
            </Space>
          </div>
          <div className="company-base-sheet-body plugin-sheet-body">
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <Spin />
            </div>
          </div>
        </>
      );
    }
    return <div style={{ textAlign: "center", padding: 48 }}><Spin /></div>;
  }
  if (!plugin.id && !item) return null;

  const viewTitle = item?.name?.trim() ? item.name : "Плагин";

  const viewBody = (
    <div className="plugin-view">
      {item?.name && !isMobile ? (
        <div className="plugin-view-title">{item.name}</div>
      ) : null}

      {hasDescription && (
        <div
          className="plugins-description-markdown plugin-view-block"
          dangerouslySetInnerHTML={{ __html: marked.parse(String(item?.description ?? "")) }}
        />
      )}

      {images.length > 0 && (
        <div className="plugin-view-block plugin-view-gallery">
          {loadingImages ? (
            <Spin />
          ) : imagesWithPhoto.length > 0 ? (
            <>
              <div className="plugins-preview-wrap plugin-view-carousel-wrap">
                <Button
                  type="text"
                  className="plugins-preview-prev"
                  icon={<LeftOutlined />}
                  disabled={currentIndex <= 0}
                  onClick={() => setSelectedImageIndex((i) => Math.max(0, i - 1))}
                />
                <img
                  src={currentImage.url}
                  alt={currentImage.name || ""}
                  className="plugins-preview-img plugin-view-carousel-img"
                />
                <Button
                  type="text"
                  className="plugins-preview-next"
                  icon={<RightOutlined />}
                  disabled={currentIndex >= imagesWithPhoto.length - 1}
                  onClick={() => setSelectedImageIndex((i) => Math.min(imagesWithPhoto.length - 1, i + 1))}
                />
              </div>
              <div className="plugins-preview-counter">
                {currentIndex + 1} / {imagesWithPhoto.length}
              </div>
              {(currentImage.name || currentImage.description) && (
                <div className="plugins-preview-description">
                  {currentImage.name && <div style={{ fontWeight: 600 }}>{currentImage.name}</div>}
                  {currentImage.description && (
                    <>
                      <span className="plugins-preview-description-label">Описание</span>
                      <div
                        className="plugins-preview-description-markdown"
                        dangerouslySetInnerHTML={{ __html: marked.parse(currentImage.description) }}
                      />
                    </>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {comments.length > 0 && (
        <div className="plugin-view-block">
          <h3>Комментарии</h3>
          {loadingComments ? (
            <Spin />
          ) : (
            <ul className="plugins-comments-list">
              {comments.map((c) => (
                <li key={c.id}>
                  <span>
                    ID {c.id}, автор {c.authorId}, {c.forAdmin ? "для админа" : ""},{" "}
                    {c.createdOn ? new Date(c.createdOn).toLocaleString() : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {files.length > 0 && !item?.needPayment && (
        <div className="plugin-view-block">
          {loadingFiles ? (
            <Spin />
          ) : (
            <>
              <ul className="plugins-files-list">
                {files.map((f) => (
                  <li key={f.fileName}>
                    <span
                      className="plugins-file-download plugins-file-download-disabled"
                      // onClick={hasSubscription ? () => downloadFile(f.fileName) : undefined}
                      role={hasSubscription ? "button" : undefined}
                      tabIndex={hasSubscription ? 0 : undefined}
                      onKeyDown={hasSubscription ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); downloadFile(f.fileName); } } : undefined}
                    >
                      {f.fileName} ({f.size != null ? `${(f.size / 1024).toFixed(1)} КБ` : ""})
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadAllFiles}
                disabled={!hasSubscription}
                style={{ marginTop: 12 }}
              >
                Скачать
              </Button>
            </>
          )}
        </div>
      )}

    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="company-base-sheet-header">
          <span className="company-base-sheet-title">{viewTitle}</span>
          <Space size="small" className="company-base-sheet-actions">
            <Button
              type="default"
              icon={<CloseOutlined />}
              onClick={onCancel}
              aria-label="Закрыть"
              className="company-base-header-icon-btn"
            />
          </Space>
        </div>
        <div className="company-base-sheet-body plugin-sheet-body">{viewBody}</div>
      </>
    );
  }

  return viewBody;
};

export default PluginView;
